/**
 * Instruction Slide-over Alpine.js Component
 *
 * Create mode: property search + terms form.
 * View mode: progress bar with 6 steps, tab content, publish action.
 */

import flatpickr from "flatpickr"
import consumer from "../channels/consumer"
import { ensureModalsLoaded } from "./property_slideover"

export function instructionSlideover() {
  return {
    open: false,
    loading: false,
    saving: false,
    publishing: false,
    publishError: '',
    formErrors: {},
    zIndex: 100010,
    mode: 'create', // 'create' or 'view'

    // View mode state
    instruction: null,
    instructionId: null,
    activeTab: 'terms',
    valuationSummary: null, // market-intelligence result for the Terms footnote

    // Terms form (instruction fields)
    form: {
      property_id: null,
      property_search: '',
      property_results: [],
      searching_properties: false,
      show_property_dropdown: false,
      selected_property: null,
      price: '',
      deposit_required: '',
      depositStatus: { message: '', status: 'no_deposit' },
      holding_fee: '',
      holdingFeeStatus: { message: '', status: 'no_fee' },
      available_from: '',
      service_type: '',
      live_at: ''
    },
    _termsSnapshot: null, // snapshot for dirty checking

    // EPC chart HTML (lazy-fetched when Compliance tab is opened)
    epcChartHtml: '',

    // Property photos (lazy-fetched when Photos tab is opened)
    photos: null, // null = not loaded yet, [] = loaded but empty
    photosLoading: false,

    // Publish mode (Go Live tab): 'now' or 'schedule'
    publishMode: 'now',

    // Advert form (property fields)
    advert: {
      headline: '',
      description: '',
      bullet_points_text: '',
      furnished: '',
      parking: '',
      outside_space: '',
      council_tax_band: ''
    },
    _advertSnapshot: null, // snapshot for dirty checking

    // Viewings form (property + viewing_setting fields)
    viewings: {
      viewing_slug: '',
      viewing_address: '',
      viewing_block_duration: 60,
      viewing_slot_duration: 15,
      viewing_blocks: [],
      notify_occupiers: false,
      viewing_notification_contacts: []
    },
    _viewingsSnapshot: null,
    showFullWeek: true,
    viewingDays: [
      { key: 'monday', label: 'Monday' },
      { key: 'tuesday', label: 'Tuesday' },
      { key: 'wednesday', label: 'Wednesday' },
      { key: 'thursday', label: 'Thursday' },
      { key: 'friday', label: 'Friday' },
      { key: 'saturday', label: 'Saturday' },
      { key: 'sunday', label: 'Sunday' }
    ],

    // Flatpickr instances
    _availableFromPicker: null,
    _liveAtPicker: null,

    // Service types (loaded from page data)
    serviceTypes: [],

    // Progress bar steps
    progressSteps: [
      { key: 'terms', label: 'Terms' },
      { key: 'advert', label: 'Advert' },
      { key: 'photos', label: 'Photos' },
      { key: 'compliance', label: 'Compliance' },
      { key: 'viewings', label: 'Viewings' },
      { key: 'go_live', label: 'Go Live' }
    ],

    init() {
      const el = document.querySelector('[data-service-types]')
      if (el) {
        try { this.serviceTypes = JSON.parse(el.dataset.serviceTypes) }
        catch (e) { console.error('Failed to parse service types:', e) }
      }

      // Subscribe to ActionCable for real-time updates (photos, EPC, certificates,
      // anything that changes the underlying property while the slideover is open).
      const self = this
      this.subscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            self.handleDataUpdate(message)
          }
        }
      )

      // EPC modal dispatches this synchronously after a successful save — pick it
      // up too so the compliance card updates without waiting for the broadcast.
      window.addEventListener('epc-updated', (e) => {
        if (this.open && this.instruction && String(this.instruction.property?.id) === String(e.detail.propertyId)) {
          this.epcChartHtml = ''
          this.refreshInstructionPropertyData()
        }
      })
    },

    destroy() {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
    },

    handleDataUpdate(message) {
      if (!this.open || !this.instruction) return
      const pid = this.instruction.property?.id
      if (!pid) return

      switch (message.type) {
        case 'property':
          if (message.id === pid) this.refreshInstructionPropertyData()
          break
        case 'epc':
          if (message.id === pid || message.data?.property_id === pid) {
            this.epcChartHtml = ''
            this.refreshInstructionPropertyData()
          }
          break
        case 'certificate':
          if (message.data?.property_id === pid) this.refreshInstructionPropertyData()
          break
        case 'landlord':
          // Fired when the agency agreement is actually sent (mark_sent flips it
          // from :drafted to :sent). Re-fetch so the Agreement card moves to
          // "Awaiting signature" without a manual page refresh.
          this.refreshAfterAgreementUpdate(message.id)
          break
      }
    },

    // Silent re-fetch of the instruction's property data — used when an
    // ActionCable broadcast tells us something on the property changed
    // (photos uploaded, EPC attached, certificate added, etc.). Only the
    // property side is replaced so any in-progress form edits in Terms /
    // Advert / Viewings are preserved.
    async refreshInstructionPropertyData() {
      if (!this.instructionId || !this.instruction) return
      try {
        const response = await fetch(`/instructions/${this.instructionId}.json`, {
          headers: { 'Accept': 'application/json' }
        })
        if (response.ok) {
          const data = await response.json()
          if (data?.property && this.instruction) {
            this.instruction.property = data.property
          }
          // Reset the lazy photo cache so the Photos tab refetches on next view
          this.photos = null
        }
      } catch (err) {
        console.error('Failed to refresh instruction property:', err)
      }
    },

    // --- Open/Close ---

    handleOpen(detail) {
      if (detail?.id) {
        this.openForView(detail.id)
      } else if (detail?.instruction) {
        this.openForView(detail.instruction.id)
      } else {
        this.openForCreate(detail?.prefill || null)
      }
    },

    openForCreate(prefill = null) {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex()

      this.mode = 'create'
      this.instruction = null
      this.instructionId = null
      this.formErrors = {}
      this.saving = false

      this.form = {
        property_id: null,
        property_search: '',
        property_results: [],
        searching_properties: false,
        show_property_dropdown: false,
        selected_property: null,
        price: '',
        deposit_required: '',
        depositStatus: { message: '', status: 'no_deposit' },
        holding_fee: '',
        holdingFeeStatus: { message: '', status: 'no_fee' },
        available_from: new Date().toISOString().split('T')[0],
        service_type: '',
        live_at: new Date().toISOString().split('T')[0],
        fee_overridden: false,
        fee_value: ''
      }

      if (prefill) {
        if (prefill.property_id && prefill.property_name) {
          this.form.property_id = prefill.property_id
          this.form.selected_property = {
            id: prefill.property_id,
            display_name: prefill.property_name,
            full_address: prefill.full_address || prefill.property_name,
            photo_url: prefill.photo_url || null,
            landlords: prefill.landlords || []
          }
        }
        if (prefill.default_service_type) this.form.service_type = prefill.default_service_type
      }

      this.open = true
      this.$nextTick(() => this.initDatePickers())
    },

    async openForView(id) {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) this.zIndex = store.getNextZIndex()

      this.mode = 'view'
      this.instructionId = id
      this.instruction = null
      this.publishError = ''
      this.activeTab = 'terms'
      this.valuationSummary = null
      this.epcChartHtml = ''
      this.photos = null
      this.photosLoading = false
      this.loading = true
      this.open = true

      try {
        const response = await fetch(`/instructions/${id}.json`, {
          headers: { 'Accept': 'application/json' }
        })
        if (response.ok) {
          this.instruction = await response.json()
          // Populate terms form from instruction
          this.form.price = this.instruction.price || ''
          this.form.deposit_required = this.instruction.deposit_required || ''
          this.form.holding_fee = this.instruction.holding_fee || ''
          this.form.available_from = this.instruction.available_from ? this.instruction.available_from.split('T')[0] : ''
          // Preserve the full datetime (date + time) from the instruction
          if (this.instruction.live_at) {
            const d = new Date(this.instruction.live_at)
            this.form.live_at = this._formatDateTimeLocal(d)
          } else {
            this.form.live_at = ''
          }
          this.form.service_type = this.instruction.service_type || ''
          this._initFeeFormFromInstruction()
          this._termsSnapshot = this._takeTermsSnapshot()
          this.updateDepositStatus()
          this.updateHoldingFeeStatus()

          // Populate advert form from property
          const p = this.instruction.property
          this.advert = {
            headline: p.headline || '',
            description: p.description || '',
            bullet_points_text: p.bullet_points_text || '',
            furnished: p.furnished || '',
            parking: p.parking || '',
            outside_space: p.outside_space || '',
            council_tax_band: p.council_tax_band || ''
          }
          this._advertSnapshot = JSON.stringify(this.advert)

          // Populate viewings form from property + viewing_setting.
          // For brand new instructions, fall back to sensible defaults so the agent
          // doesn't have to fill in obvious fields.
          const hasViewingSetup = !!(p.viewing_slug || p.viewing_blocks?.length > 0)
          this.viewings = {
            viewing_slug: p.viewing_slug || p.suggested_viewing_slug || '',
            viewing_address: p.viewing_address || p.suggested_viewing_address || '',
            viewing_block_duration: p.viewing_block_duration || 60,
            viewing_slot_duration: p.viewing_slot_duration || 15,
            viewing_blocks: p.viewing_blocks || [],
            // Default occupier notifications ON for new setups
            notify_occupiers: hasViewingSetup ? !!p.notify_occupiers : true,
            viewing_notification_contacts: p.viewing_notification_contacts || []
          }
          this._viewingsSnapshot = JSON.stringify(this.viewings)

          this.$nextTick(() => this.initDatePickers())
          this._loadValuationSummary(id) // fire-and-forget; feeds the Terms footnote
        } else {
          console.error('Failed to load instruction:', response.status)
        }
      } catch (error) {
        console.error('Error loading instruction:', error)
      } finally {
        this.loading = false
      }
    },

    async close() {
      // Auto-save dirty changes for draft instructions so nothing is lost
      if (this.mode === 'view' && this.instruction?.status === 'draft') {
        if (this.termsDirty) await this.saveTerms()
        if (this.advertDirty) await this.saveAdvert()
        if (this.viewingsDirty) await this.saveViewings()
      }
      this.destroyDatePickers()
      this.open = false
    },

    // --- Date pickers (create mode) ---

    initDatePickers() {
      this.destroyDatePickers()
      const pickerConfig = {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'j M Y',
        appendTo: document.body,
        minDate: 'today'
      }

      if (this.$refs.availableFromInput) {
        this._availableFromPicker = flatpickr(this.$refs.availableFromInput, {
          ...pickerConfig,
          defaultDate: this.form.available_from || null,
          onChange: (selectedDates, dateStr) => { this.form.available_from = dateStr }
        })
      }
    },

    initLiveAtPicker() {
      this._liveAtPicker?.destroy()
      this._liveAtPicker = null
      if (!this.$refs.liveAtInput) return

      // Default to 10am if no live_at is set, if it's date-only, or if the time is exactly midnight
      let defaultDate = this.form.live_at
      const isDateOnly = defaultDate && /^\d{4}-\d{2}-\d{2}$/.test(String(defaultDate))
      const isMidnight = defaultDate && /(\b00:00|T00:00:00)/.test(String(defaultDate))
      const needsDefault = !defaultDate || isDateOnly || isMidnight
      if (needsDefault) {
        const target = defaultDate ? new Date(defaultDate) : new Date()
        if (!defaultDate) target.setDate(target.getDate() + 1)
        target.setHours(10, 0, 0, 0)
        defaultDate = target
        this.form.live_at = this._formatDateTimeLocal(target)
      }

      this._liveAtPicker = flatpickr(this.$refs.liveAtInput, {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        altInput: true,
        altFormat: 'j M Y \\a\\t H:i',
        time_24hr: true,
        appendTo: document.body,
        minDate: 'today',
        defaultDate: defaultDate,
        defaultHour: 10,
        defaultMinute: 0,
        minuteIncrement: 15,
        onChange: (selectedDates, dateStr) => { this.form.live_at = dateStr }
      })
    },

    // Format a Date as "YYYY-MM-DD HH:mm" for flatpickr's enableTime mode
    _formatDateTimeLocal(date) {
      const pad = (n) => n.toString().padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    destroyDatePickers() {
      this._availableFromPicker?.destroy()
      this._availableFromPicker = null
      this._liveAtPicker?.destroy()
      this._liveAtPicker = null
    },

    // --- Helpers ---

    getInitials(name) {
      if (!name) return '?'
      name = name.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ')
      return name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
    },

    // Auto-resize a textarea to fit its content
    autoResize(el) {
      if (!el) return
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    },

    // Resize both advert textareas (called when Advert tab becomes active)
    resizeAdvertTextareas() {
      this.autoResize(this.$refs.advertDescription)
      this.autoResize(this.$refs.advertFeatures)
    },

    // Fetch property photos lazily (called when Photos tab is opened)
    async fetchPhotos() {
      if (!this.instructionId || this.photosLoading || this.photos !== null) return
      this.photosLoading = true
      try {
        const response = await fetch(`/instructions/${this.instructionId}/photos.json`, {
          headers: { 'Accept': 'application/json' }
        })
        if (response.ok) {
          const data = await response.json()
          this.photos = data.photos || []
        } else {
          this.photos = []
        }
      } catch (error) {
        console.error('Failed to fetch photos:', error)
        this.photos = []
      } finally {
        this.photosLoading = false
      }
    },

    // Fetch the EPC chart SVG (called when Compliance tab is opened)
    async fetchEpcChart() {
      const propertyId = this.instruction?.property?.id
      if (!propertyId) return
      try {
        const response = await fetch(`/properties/${propertyId}/epc_chart`, {
          headers: { 'Accept': 'text/html' }
        })
        if (response.ok) {
          const html = await response.text()
          const match = html.match(/<svg[\s\S]*<\/svg>/)
          this.epcChartHtml = match ? match[0] : ''
        }
      } catch (error) {
        console.error('Failed to fetch EPC chart:', error)
      }
    },

    // Open the EPC modal (used in Compliance tab)
    openEpcModal() {
      if (!window.openEpcModal) return
      const epc = this.instruction?.property?.epc
      const mapped = epc ? {
        current_energy_rating: epc.current_rating,
        certificate_number: epc.certificate_number,
        certificate_url: epc.certificate_url,
        full_certificate_pdf_url: epc.full_certificate_pdf_url,
        api_retrieved_at: epc.api_sourced ? true : null,
        user_override: !epc.api_sourced,
        date_expires: epc.date_expires,
        address: epc.address
      } : null
      window.openEpcModal(this.instruction.property.id, this.instruction.property.full_address, mapped)
    },

    // Open the property photo management modal (used in Photos tab)
    async openPhotoManageModal() {
      const propertyId = this.instruction?.property?.id
      if (!propertyId) return
      // Make sure the property slideover modals (including photo modal) are loaded
      await ensureModalsLoaded()
      window._currentPropertyId = propertyId
      if (typeof window.updatePhotoFormActions === 'function') {
        window.updatePhotoFormActions(propertyId)
      }
      Alpine.store('modals').isPhotoManageModalOpen = true
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return '—'
      return '£' + Math.round(amount).toLocaleString()
    },

    formatDate(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    // --- Unified display properties (work across create and view modes) ---

    get hasContext() {
      // Show progress bar/stats/tabs once we have a property (create) or instruction (view)
      if (this.mode === 'create') return !!this.form.selected_property
      return !!this.instruction
    },

    get displayTitle() {
      if (this.mode === 'create') return this.form.selected_property?.display_name || 'New Instruction'
      return this.instruction?.property?.display_name || 'Instruction'
    },

    get displaySubtitle() {
      if (this.mode === 'create') {
        const parts = []
        const landlord = this.form.selected_property?.landlords?.[0]?.name
        if (landlord) parts.push(landlord)
        const st = this.serviceTypes.find(s => s.value === this.form.service_type)
        if (st) parts.push(st.label)
        return parts.length > 0 ? parts.join(' \u00B7 ') : null
      }
      if (this.mode === 'view' && this.instruction) {
        const parts = []
        const landlord = this.instruction.property?.landlords?.[0]?.name
        if (landlord) parts.push(landlord)
        if (this.instruction.service_type_display) parts.push(this.instruction.service_type_display)
        return parts.length > 0 ? parts.join(' \u00B7 ') : null
      }
      return null
    },

    get displayPrice() {
      // Always use form data — it's populated from instruction in view mode
      return this.form.price ? this.formatCurrency(parseFloat(this.form.price)) : '\u2014'
    },

    get displayAvailableFrom() {
      return this.form.available_from ? this.formatDate(this.form.available_from) : '\u2014'
    },

    get displayLiveAt() {
      // Only show the Go Live date once the instruction is actually scheduled or live on portals.
      // Draft instructions don't have a meaningful go-live date — it's just a working selection.
      if (this.mode === 'view' && this.instruction) {
        const status = this.instruction.status
        if (status === 'scheduled') return this.form.live_at ? this.formatDateTime(this.form.live_at) : '\u2014'
        if (status === 'active' || status === 'let_agreed') return this.form.live_at ? this.formatDate(this.form.live_at) : '\u2014'
        return '\u2014'
      }
      return this.form.live_at ? this.formatDate(this.form.live_at) : '\u2014'
    },

    formatDateTime(dateString) {
      if (!dateString) return '\u2014'
      const date = new Date(dateString)
      const datePart = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      return `${datePart} \u00B7 ${timePart}`
    },

    // --- Market valuation (Terms footnote + full modal) ---

    async _loadValuationSummary(id) {
      try {
        const resp = await fetch(`/instructions/${id}/valuation.json`, { headers: { Accept: 'application/json' } })
        if (!resp.ok) return
        const data = await resp.json()
        if (this.instructionId !== id) return // slideover moved on to another instruction
        this.valuationSummary = data.result || null
      } catch (_) {
        // Footnote stays bare \u2014 the modal still works on demand.
      }
    },

    get valuationHeadline() {
      const r = this.valuationSummary
      if (!r) return null
      const pence = r.median_let_agreed_pence || r.median_pence || r.median_asking_pence || r.median_portfolio_pence
      if (!pence) return null
      return '\u00A3' + Math.round(pence / 100).toLocaleString('en-GB')
    },

    openValuationModal() {
      if (!this.instructionId) return
      window.dispatchEvent(new CustomEvent('open-valuation-modal', { detail: { instructionId: this.instructionId } }))
    },

    fmtMoney0(v) {
      const n = parseFloat(v)
      if (isNaN(n)) return ''
      return n.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    // --- Progress bar ---

    stepComplete(stepKey) {
      if (this.mode === 'create') {
        if (stepKey === 'terms') return !!this.form.price
        return false
      }
      if (!this.instruction) return false
      const p = this.instruction.property
      switch (stepKey) {
        case 'terms':
          return !!this.form.price
        case 'advert':
          return !!(this.advert.description && this.advert.headline)
        case 'photos':
          return p?.property_photos_count > 0
        case 'compliance':
          return !!p?.has_valid_epc
        case 'viewings':
          return !!(this.viewings.viewing_slug && this.viewings.viewing_blocks?.length > 0)
        case 'go_live':
          return !!this.instruction.published
        default:
          return false
      }
    },

    get progressPercent() {
      if (!this.instruction) return 0
      let lastComplete = 0
      for (let i = 0; i < this.progressSteps.length; i++) {
        if (this.stepComplete(this.progressSteps[i].key)) lastComplete = i
      }
      return (lastComplete / (this.progressSteps.length - 1)) * 100
    },

    // --- Readiness checks (Go Live tab) ---

    get readinessChecks() {
      if (!this.instruction) return []
      const p = this.instruction.property
      return [
        { key: 'price', label: this.instruction.price ? `Rent set at ${this.formatCurrency(this.instruction.price)}/mo` : 'Monthly rent not set', pass: !!this.instruction.price, tab: 'terms' },
        { key: 'deposit', label: this.instruction.deposit_required ? `Deposit set at ${this.formatCurrency(this.instruction.deposit_required)}` : 'Deposit not set', pass: !!this.instruction.deposit_required, tab: 'terms' },
        { key: 'headline', label: this.advert.headline ? 'Headline set' : 'Headline is missing', pass: !!this.advert.headline, tab: 'advert' },
        { key: 'description', label: this.advert.description ? 'Description set' : 'Description is missing', pass: !!this.advert.description, tab: 'advert' },
        { key: 'photos', label: p?.property_photos_count > 0 ? `${p.property_photos_count} photo${p.property_photos_count !== 1 ? 's' : ''} uploaded` : 'No photos uploaded', pass: p?.property_photos_count > 0, tab: 'photos' },
        { key: 'epc', label: p?.has_valid_epc ? `Valid EPC (${p.epc_rating || 'rated'})` : 'EPC missing or expired', pass: !!p?.has_valid_epc, tab: 'compliance' },
        { key: 'viewings', label: this.viewings.viewing_blocks?.length > 0 ? 'Viewing times configured' : 'No viewing times set', pass: this.viewings.viewing_blocks?.length > 0, tab: 'viewings' },
        { key: 'slug', label: this.viewings.viewing_slug ? 'Booking URL set' : 'Booking URL not set', pass: !!this.viewings.viewing_slug, tab: 'viewings' }
      ]
    },

    get allChecksPass() {
      return this.readinessChecks.every(c => c.pass)
    },

    // An off-market (direct/landlord-introduced) let: active but never
    // advertised. Applications still work via its private apply link.
    get isOffMarketActive() {
      return !!this.instruction?.off_market &&
        ['active', 'let_agreed'].includes(this.instruction?.status)
    },

    // Off-market only needs a rent to set up the tenancy — no advert copy,
    // photos or EPC marketing, since nothing gets published.
    get offMarketReady() {
      return !!this.instruction?.price
    },

    // --- Actions ---

    // --- Dirty tracking ---

    _takeTermsSnapshot() {
      return JSON.stringify({
        price: this.form.price,
        deposit_required: this.form.deposit_required,
        holding_fee: this.form.holding_fee,
        available_from: this.form.available_from,
        service_type: this.form.service_type,
        live_at: this.form.live_at,
        fee_overridden: this.form.fee_overridden,
        fee_value: this.form.fee_value
      })
    },

    _initFeeFormFromInstruction() {
      const i = this.instruction
      if (!i) return
      const unit = this._feeUnitFor(i.service_type)
      if (unit === 'percent' && i.fee_percentage != null) {
        this.form.fee_overridden = true
        this.form.fee_value = String(i.fee_percentage)
      } else if (unit === 'pence' && i.fee_pence != null) {
        this.form.fee_overridden = true
        this.form.fee_value = (i.fee_pence / 100).toFixed(2)
      } else {
        this.form.fee_overridden = false
        this.form.fee_value = ''
      }
    },

    _feeUnitFor(serviceType) {
      if (!serviceType) return null
      // Tenant find is always pence (fixed £). Other types are % of rent.
      // (Mirrors agency.service_type_manages_rent? — kept simple here to
      // avoid round-tripping the agency config to the client.)
      return serviceType === 'tenant_find' ? 'pence' : 'percent'
    },

    get feeUnit() { return this._feeUnitFor(this.form.service_type) },
    get isFeePercent() { return this.feeUnit === 'percent' },
    get isFeePence()   { return this.feeUnit === 'pence' },

    // Cascade resolution for the *currently selected* service type (which
    // may differ from the saved one if the agent is editing). Falls back
    // to the saved effective_fee for the rare case where the cascade map
    // hasn't been shipped yet.
    get currentCascade() {
      const map = this.instruction?.fee_cascade_by_service_type
      if (map && this.form.service_type) return map[this.form.service_type] || null
      return this.instruction?.effective_fee || null
    },

    // Effective fee for display: respects the unsaved override toggle/value
    // but falls back to the server-resolved cascade when not overriding.
    // Always returned in the canonical unit for the panel: percent as a
    // number (e.g. 10.0), pence as an integer (e.g. 69500).
    get displayedFeeValue() {
      if (this.form.fee_overridden) {
        const n = parseFloat(this.form.fee_value)
        if (isNaN(n)) return null
        // Override input is in % (e.g. "10") or £ (e.g. "695.00") —
        // normalise pence to the canonical pence integer.
        return this.isFeePence ? Math.round(n * 100) : n
      }
      return this.currentCascade?.value ?? null
    },

    // Source pill — only rendered when the cascade value is in effect; an
    // override announces itself via the revert link instead of a badge.
    get displayedFeeSourceLabel() {
      return this.currentCascade?.source_label || 'No fee set'
    },

    get feeHeadline() {
      const v = this.displayedFeeValue
      if (v == null) return '—'
      if (this.isFeePercent) return `${Number(v).toFixed(1)}%`
      // pence
      return '£' + (Number(v) / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    get feeHeadlineSuffix() {
      if (this.displayedFeeValue == null) return ''
      return this.isFeePercent ? 'of rent collected' : 'one-off, fixed'
    },

    // One muted line under the fee headline: derived £/mo · £/yr for
    // percentage fees, rent-equivalent context for fixed tenant-find fees.
    get feeSubline() {
      const v = this.displayedFeeValue
      const rent = parseFloat(this.form.price)
      if (v == null) return ''
      if (this.isFeePercent) {
        if (isNaN(rent) || rent <= 0) return ''
        const monthly = rent * v / 100
        const fmt = (n) => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        return `${fmt(monthly)} /mo · ${fmt(monthly * 12)} /yr`
      }
      if (!isNaN(rent) && rent > 0) {
        const weeks = ((v / 100) / (rent / 4.33)).toFixed(1)
        return `≈ ${weeks} weeks' rent · charged when the tenant moves in`
      }
      return 'Charged when the tenant moves in'
    },

    // Cascade value formatted for the revert link (ignores any override)
    get cascadeFeeHeadline() {
      const v = this.currentCascade?.value
      if (v == null) return null
      if (this.isFeePercent) return `${Number(v).toFixed(1)}%`
      return '£' + (Number(v) / 100).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    // "Revert to landlord default (9.0%)" — source only, without the
    // "· Susan Wincott" suffix the cascade labels carry. When there is no
    // fallback fee to revert to, the override can only be removed.
    get revertLabel() {
      const c = this.currentCascade
      if (c?.value == null) return 'Remove override'
      const source = (c.source_label || 'default').split('·')[0].trim().toLowerCase()
      return `Revert to ${source} (${this.cascadeFeeHeadline})`
    },

    toggleFeeOverride() {
      if (this.form.fee_overridden) {
        // Turning off: clear input
        this.form.fee_overridden = false
        this.form.fee_value = ''
      } else {
        // Turning on: prefill with the currently displayed cascade value
        const cascade = this.currentCascade
        if (cascade?.value != null) {
          this.form.fee_value = this.isFeePercent ? String(cascade.value) : (cascade.value / 100).toFixed(2)
        }
        this.form.fee_overridden = true
      }
    },

    onServiceTypeChanged() {
      // Unit changes when service_type switches between rent-managing and not.
      // Reset the override so we don't carry a percentage value into a pence
      // field (or vice versa). Persisted with the rest of the Terms form via
      // the dirty-state Save bar — one editing model for the whole tab.
      this.form.fee_overridden = false
      this.form.fee_value = ''
    },

    get termsDirty() {
      if (!this._termsSnapshot) return false
      return this._takeTermsSnapshot() !== this._termsSnapshot
    },

    get advertDirty() {
      if (!this._advertSnapshot) return false
      return JSON.stringify(this.advert) !== this._advertSnapshot
    },

    get viewingsDirty() {
      if (!this._viewingsSnapshot) return false
      return JSON.stringify(this.viewings) !== this._viewingsSnapshot
    },

    // --- Viewing block helpers ---

    timeToMinutes(time) {
      if (!time) return 0
      const [hours, minutes] = time.split(':').map(Number)
      return hours * 60 + minutes
    },

    minutesToTime(minutes) {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
    },

    addViewingBlock(day) {
      const blockDuration = parseInt(this.viewings.viewing_block_duration) || 60
      const existingBlocks = (this.viewings.viewing_blocks || []).filter(b => b.day === day)
      let startTime = '10:00'
      if (existingBlocks.length > 0) {
        startTime = existingBlocks[existingBlocks.length - 1].end
      }
      const startMinutes = this.timeToMinutes(startTime)
      const endMinutes = startMinutes + blockDuration
      const newBlock = { day, start: startTime, end: this.minutesToTime(endMinutes) }
      const blocks = [...(this.viewings.viewing_blocks || []), newBlock]
      this._sortViewingBlocks(blocks)
      this.viewings.viewing_blocks = blocks
    },

    removeViewingBlock(day, startTime) {
      this.viewings.viewing_blocks = (this.viewings.viewing_blocks || []).filter(
        b => !(b.day === day && b.start === startTime)
      )
    },

    updateViewingBlockTime(day, indexInDay, field, newValue) {
      const dayBlocks = (this.viewings.viewing_blocks || []).filter(b => b.day === day)
      if (indexInDay < 0 || indexInDay >= dayBlocks.length) return
      const blockToUpdate = dayBlocks[indexInDay]
      const allBlocks = [...(this.viewings.viewing_blocks || [])]
      const fullIndex = allBlocks.findIndex(b => b.day === day && b.start === blockToUpdate.start && b.end === blockToUpdate.end)
      if (fullIndex !== -1) {
        allBlocks[fullIndex] = { ...allBlocks[fullIndex], [field]: newValue }
        this._sortViewingBlocks(allBlocks)
        this.viewings.viewing_blocks = allBlocks
      }
    },

    _sortViewingBlocks(blocks) {
      const dayOrder = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 }
      blocks.sort((a, b) => {
        if (dayOrder[a.day] !== dayOrder[b.day]) return dayOrder[a.day] - dayOrder[b.day]
        return this.timeToMinutes(a.start) - this.timeToMinutes(b.start)
      })
    },

    addNotificationContact() {
      this.viewings.viewing_notification_contacts = [
        ...(this.viewings.viewing_notification_contacts || []),
        { email: '', phone: '' }
      ]
    },

    removeNotificationContact(index) {
      const list = [...(this.viewings.viewing_notification_contacts || [])]
      list.splice(index, 1)
      this.viewings.viewing_notification_contacts = list
    },

    // --- Save/Cancel Terms ---

    async saveTerms() {
      if (!this.instructionId || this.saving) return
      this.saving = true

      try {
        const response = await fetch(`/instructions/${this.instructionId}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            instruction: {
              price: this.form.price || null,
              deposit_required: this.form.deposit_required || null,
              holding_fee: this.form.holding_fee || null,
              available_from: this.form.available_from || null,
              service_type: this.form.service_type || null,
              live_at: this.form.live_at || null,
              ...this._feePayload()
            }
          })
        })

        const data = await response.json()
        if (response.ok && data.success) {
          this.instruction = { ...this.instruction, ...data.instruction, property: this.instruction.property }
          this.afterSaveTermsRefresh()
          this._termsSnapshot = this._takeTermsSnapshot()
          window.dispatchEvent(new CustomEvent('instruction-saved', { detail: { instruction: data.instruction } }))
        } else {
          this.formErrors.base = data.errors?.join(', ') || 'Failed to save'
        }
      } catch (error) {
        console.error('Save terms error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    },

    _feePayload() {
      // Persist the override on the column matching the service type's unit.
      // Always null the inactive column so a unit-change cleans up cleanly.
      if (!this.form.fee_overridden) {
        return { fee_percentage: null, fee_pence: null }
      }
      const raw = parseFloat(this.form.fee_value)
      if (isNaN(raw)) return { fee_percentage: null, fee_pence: null }
      if (this.isFeePercent) {
        return { fee_percentage: raw, fee_pence: null }
      }
      return { fee_percentage: null, fee_pence: Math.round(raw * 100) }
    },

    cancelTerms() {
      if (!this._termsSnapshot) return
      const snap = JSON.parse(this._termsSnapshot)
      Object.assign(this.form, snap)
      this.updateDepositStatus()
      this.updateHoldingFeeStatus()
      // Update the existing flatpickr instance with the restored value
      if (this._availableFromPicker) {
        this._availableFromPicker.setDate(this.form.available_from || null, false)
      }
    },

    async refreshAfterAgreementUpdate(landlordId) {
      if (!this.open || this.mode !== 'view' || !this.instructionId) return
      if (this.instruction?.landlord_agreement?.landlord_id !== landlordId) return
      try {
        const r = await fetch(`/instructions/${this.instructionId}.json`, { headers: { 'Accept': 'application/json' } })
        if (r.ok) {
          const data = await r.json()
          this.instruction = { ...this.instruction, landlord_agreement: data.landlord_agreement }
        }
      } catch (e) {
        console.error('Failed to refresh agreement state:', e)
      }
    },

    afterSaveTermsRefresh() {
      // After a successful save the server-side effective_fee may have shifted
      // (e.g. unit changed because service_type changed). Re-init the fee form
      // so the next snapshot reflects the latest persisted state.
      this._initFeeFormFromInstruction()
    },

    // --- Save/Cancel Advert ---

    async saveAdvert() {
      if (!this.instruction?.property?.id || this.saving) return
      this.saving = true

      try {
        const response = await fetch(`/properties/${this.instruction.property.id}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            property: {
              headline: this.advert.headline,
              description: this.advert.description,
              bullet_points_text: this.advert.bullet_points_text,
              furnished: this.advert.furnished,
              parking: this.advert.parking,
              outside_space: this.advert.outside_space,
              council_tax_band: this.advert.council_tax_band
            }
          })
        })

        if (response.ok) {
          // Update the instruction's property data in place
          this.instruction.property.headline = this.advert.headline
          this.instruction.property.description = this.advert.description
          this.instruction.property.bullet_points_text = this.advert.bullet_points_text
          this.instruction.property.furnished = this.advert.furnished
          this.instruction.property.parking = this.advert.parking
          this.instruction.property.outside_space = this.advert.outside_space
          this.instruction.property.council_tax_band = this.advert.council_tax_band
          this._advertSnapshot = JSON.stringify(this.advert)
        } else {
          const data = await response.json()
          this.formErrors.base = data.errors?.join(', ') || 'Failed to save advert'
        }
      } catch (error) {
        console.error('Save advert error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    },

    cancelAdvert() {
      if (!this._advertSnapshot) return
      this.advert = JSON.parse(this._advertSnapshot)
    },

    // --- Save/Cancel Viewings ---

    async saveViewings() {
      if (!this.instruction?.property?.id || this.saving) return
      this.saving = true

      try {
        const formData = new FormData()
        formData.append('property[viewing_slug]', this.viewings.viewing_slug)
        formData.append('property[viewing_address]', this.viewings.viewing_address)
        formData.append('viewing_settings', JSON.stringify({
          viewing_block_duration: this.viewings.viewing_block_duration,
          viewing_slot_duration: this.viewings.viewing_slot_duration,
          viewing_blocks: this.viewings.viewing_blocks,
          notify_occupiers: this.viewings.notify_occupiers,
          viewing_notification_contacts: this.viewings.viewing_notification_contacts
        }))

        const response = await fetch(`/properties/${this.instruction.property.id}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: formData
        })

        if (response.ok) {
          this.instruction.property.viewing_slug = this.viewings.viewing_slug
          this.instruction.property.viewing_address = this.viewings.viewing_address
          this.instruction.property.viewing_block_duration = this.viewings.viewing_block_duration
          this.instruction.property.viewing_slot_duration = this.viewings.viewing_slot_duration
          this.instruction.property.viewing_blocks = this.viewings.viewing_blocks
          this.instruction.property.notify_occupiers = this.viewings.notify_occupiers
          this.instruction.property.viewing_notification_contacts = this.viewings.viewing_notification_contacts
          this._viewingsSnapshot = JSON.stringify(this.viewings)
        } else {
          this.formErrors.base = 'Failed to save viewings'
        }
      } catch (error) {
        console.error('Save viewings error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    },

    cancelViewings() {
      if (!this._viewingsSnapshot) return
      this.viewings = JSON.parse(this._viewingsSnapshot)
    },

    // --- Status transitions (kebab menu) ---

    confirmWithdraw() {
      if (!confirm("Withdraw this instruction? The property will be removed from portals.")) return
      this.transitionTo('withdrawn')
    },

    // Take an off-market active instruction offline and back to draft. Clears
    // off_market too so the draft is a clean slate — otherwise a later regular
    // "Publish now" (which never touches off_market) would silently re-create
    // an active-but-off-market instruction.
    returnToDraft() {
      if (!confirm("Take this instruction offline and return it to draft? It will stop accepting applications via its private apply link.")) return
      this.transitionTo('draft', { off_market: false })
    },

    async transitionTo(newStatus, extra = {}) {
      if (!this.instructionId || this.saving) return
      this.saving = true
      try {
        const response = await fetch(`/instructions/${this.instructionId}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ instruction: { status: newStatus, ...extra } })
        })

        const data = await response.json()
        if (response.ok && data.success) {
          // Refresh instruction data from server (mirror has fired, property updated)
          await this.openForView(this.instructionId)
          window.dispatchEvent(new CustomEvent('instruction-saved', { detail: { instruction: data.instruction } }))
        } else {
          this.formErrors.base = data.errors?.join(', ') || 'Failed to update status'
        }
      } catch (error) {
        console.error('Status transition error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    },

    async publish() {
      if (!this.allChecksPass || this.publishing) return
      // Schedule mode requires a date to be picked
      if (this.publishMode === 'schedule' && !this.form.live_at) return
      this.publishError = ''
      this.publishing = true

      // "Now" → status=active, set published_at and live_at to now
      // "Schedule" → status=scheduled, set live_at to chosen date/time
      const payload = this.publishMode === 'now'
        ? { instruction: { status: 'active', published_at: new Date().toISOString(), live_at: new Date().toISOString() } }
        : { instruction: { status: 'scheduled', live_at: this.form.live_at } }

      try {
        const response = await fetch(`/instructions/${this.instructionId}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payload)
        })

        const data = await response.json()
        if (response.ok && data.success) {
          // Refresh the instruction data
          await this.openForView(this.instructionId)
          this.activeTab = 'go_live'
          window.dispatchEvent(new CustomEvent('instruction-saved', { detail: { instruction: data.instruction } }))
        } else {
          this.publishError = (data.errors || []).join('. ') || "That didn't save. Please try again."
        }
      } catch (error) {
        console.error('Publish error:', error)
        this.publishError = "That didn't save. Please check your connection and try again."
      } finally {
        this.publishing = false
      }
    },

    // Make the instruction active off-market: live for applications via its
    // private apply link, but never pushed to portals or the agency website.
    async publishOffMarket() {
      if (!this.offMarketReady || this.publishing) return
      this.publishing = true

      const payload = { instruction: { status: 'active', off_market: true, published_at: new Date().toISOString(), live_at: new Date().toISOString() } }

      try {
        const response = await fetch(`/instructions/${this.instructionId}`, {
          method: 'PATCH',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payload)
        })

        const data = await response.json()
        if (response.ok && data.success) {
          await this.openForView(this.instructionId)
          this.activeTab = 'go_live'
          window.dispatchEvent(new CustomEvent('instruction-saved', { detail: { instruction: data.instruction } }))
        } else {
          this.formErrors.base = data.errors?.join(', ') || 'Failed to make off-market'
        }
      } catch (error) {
        console.error('Off-market publish error:', error)
      } finally {
        this.publishing = false
      }
    },

    // --- Property search (create mode) ---

    async searchProperties() {
      const query = this.form.property_search.trim()
      if (query.length < 2) {
        this.form.property_results = []
        this.form.show_property_dropdown = false
        return
      }
      this.form.searching_properties = true
      try {
        const response = await fetch(`/api/properties/search?q=${encodeURIComponent(query)}`, { headers: { 'Accept': 'application/json' } })
        const data = await response.json()
        this.form.property_results = data
        this.form.show_property_dropdown = true
      } catch (error) {
        console.error('Property search error:', error)
      } finally {
        this.form.searching_properties = false
      }
    },

    async selectProperty(property) {
      this.form.property_search = ''
      this.form.property_results = []
      this.form.show_property_dropdown = false

      // Auto-create the instruction for this property
      this.saving = true
      try {
        const response = await fetch('/instructions', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({
            instruction: {
              property_id: property.id,
              service_type: property.default_service_type || null,
              live_at: new Date().toISOString()
            }
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          window.dispatchEvent(new CustomEvent('instruction-saved', {
            detail: { instruction: data.instruction, isNew: true }
          }))
          // Switch to view mode for the newly created instruction
          await this.openForView(data.instruction.id)
          this.activeTab = 'terms'
        } else {
          // Show error (e.g. "property already has an active instruction")
          this.formErrors.base = data.errors?.join(', ') || 'Failed to create instruction'
        }
      } catch (error) {
        console.error('Auto-create error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    },

    clearProperty() {
      this.form.selected_property = null
      this.form.property_id = null
      this.form.service_type = ''
    },

    // --- Deposit calculations ---

    calculateStandardDeposit(monthlyPrice) {
      if (!monthlyPrice || monthlyPrice <= 0) return 0
      return Math.floor((monthlyPrice * 12) / 52.0 * 5)
    },

    calculateMaximumLegalDeposit(monthlyPrice) {
      if (!monthlyPrice || monthlyPrice <= 0) return 0
      const annualRent = monthlyPrice * 12
      const weeks = annualRent >= 50000 ? 6 : 5
      return Math.floor((annualRent / 52.0) * weeks)
    },

    updateDepositFromPrice() {
      const monthlyPrice = parseFloat(this.form.price) || 0
      if (monthlyPrice > 0) this.form.deposit_required = this.calculateStandardDeposit(monthlyPrice)
    },

    updateDepositStatus() {
      const monthlyPrice = parseFloat(this.form.price) || 0
      const depositAmount = parseFloat(this.form.deposit_required) || 0
      if (!depositAmount || depositAmount <= 0) {
        this.form.depositStatus = { message: '', status: 'no_deposit' }
        return
      }
      const maxLegal = this.calculateMaximumLegalDeposit(monthlyPrice)
      const maxWeeks = (monthlyPrice * 12) >= 50000 ? 6 : 5
      const cap = `${maxWeeks}-week cap \u00B7 max \u00A3${maxLegal.toLocaleString('en-GB')}`
      if (depositAmount > maxLegal) {
        this.form.depositStatus = { message: `Over ${cap}`, status: 'exceeds_limit' }
      } else {
        this.form.depositStatus = { message: `Within ${cap}`, status: 'within_limit' }
      }
    },

    // --- Holding fee calculations ---

    calculateStandardHoldingFee(monthlyPrice) {
      if (!monthlyPrice || monthlyPrice <= 0) return 0
      return Math.floor((monthlyPrice * 12) / 52.0)
    },

    calculateMaximumLegalHoldingFee(monthlyPrice) {
      if (!monthlyPrice || monthlyPrice <= 0) return 0
      return Math.floor((monthlyPrice * 12) / 52.0)
    },

    updateHoldingFeeFromPrice() {
      const monthlyPrice = parseFloat(this.form.price) || 0
      if (monthlyPrice > 0) this.form.holding_fee = this.calculateStandardHoldingFee(monthlyPrice)
    },

    updateHoldingFeeStatus() {
      const monthlyPrice = parseFloat(this.form.price) || 0
      const feeAmount = parseFloat(this.form.holding_fee) || 0
      if (!feeAmount || feeAmount <= 0) {
        this.form.holdingFeeStatus = { message: '', status: 'no_fee' }
        return
      }
      const maxLegal = this.calculateMaximumLegalHoldingFee(monthlyPrice)
      const cap = `1-week cap \u00B7 max \u00A3${maxLegal.toLocaleString('en-GB')}`
      if (feeAmount > maxLegal) {
        this.form.holdingFeeStatus = { message: `Over ${cap}`, status: 'exceeds_limit' }
      } else {
        this.form.holdingFeeStatus = { message: `Within ${cap}`, status: 'within_limit' }
      }
    },

    // --- Validation & Save (create/edit mode) ---

    validateForm() {
      this.formErrors = {}
      if (!this.form.property_id) this.formErrors.property = 'Please select a property'
      return Object.keys(this.formErrors).length === 0
    },

    async save() {
      if (!this.validateForm()) return
      this.saving = true
      this.formErrors = {}

      const payload = {
        instruction: {
          property_id: this.form.property_id,
          price: this.form.price || null,
          deposit_required: this.form.deposit_required || null,
          holding_fee: this.form.holding_fee || null,
          available_from: this.form.available_from || null,
          service_type: this.form.service_type || null,
          live_at: this.form.live_at || null
        }
      }

      const isEdit = this.mode === 'edit' && this.instructionId
      const url = isEdit ? `/instructions/${this.instructionId}` : '/instructions'
      const method = isEdit ? 'PATCH' : 'POST'

      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(payload)
        })

        const data = await response.json()

        if (response.ok && data.success) {
          window.dispatchEvent(new CustomEvent('instruction-saved', {
            detail: { instruction: data.instruction, isNew: !isEdit }
          }))

          if (isEdit) {
            // Return to view mode
            await this.openForView(this.instructionId)
          } else {
            // Open the newly created instruction in view mode
            await this.openForView(data.instruction.id)
          }
        } else {
          if (data.errors && Array.isArray(data.errors)) {
            this.formErrors.base = data.errors.join(', ')
          } else {
            this.formErrors.base = 'Failed to save instruction'
          }
        }
      } catch (error) {
        console.error('Save error:', error)
        this.formErrors.base = 'An unexpected error occurred.'
      } finally {
        this.saving = false
      }
    }
  }
}
